import {
  plans,
  addOns,
  CYCLE_MONTHS,
  addOnPriceIdForCycle,
  planPriceIdForCycle,
} from "@/config/stripePlans";
import type { OnboardingData } from "./types";

export const EARLY_SUPPORTER_PLAN = "early_supporter";

// The two comped grants shown when the Early Supporter Plan is selected.
export const EARLY_SUPPORTER_GRANTS = [
  {
    key: "dibs",
    name: "Early Supporter Plan - Dibs",
    description: "The Growth plan — given free.",
  },
  {
    key: "dibs_booking",
    name: "Early Supporter Plan - Dibs Booking",
    description: `The Dibs Bookings add-on (S$${addOns.bookings?.price.monthly ?? 20}/mo) — given free.`,
  },
] as const;

export function isEarlySupporter(data: OnboardingData): boolean {
  return data.selectedPlan === EARLY_SUPPORTER_PLAN;
}

// Whether this client is being set up WITH Dibs Bookings (the paid add-on, or
// the comped Early Supporter "dibs_booking" grant). When false, the client is
// AI-assistant-only: the agent runs in answer-only mode (no booking tools) and
// the operator connects the client's own data sources / actions afterwards.
// This is the booking-vs-AI-only fork for the internal wizard.
export function clientUsesBookings(data: OnboardingData): boolean {
  if (isEarlySupporter(data)) {
    return data.earlySupporterGrants.includes("dibs_booking");
  }
  return data.selectedAddOns.includes("bookings");
}

// Bookings is the only add-on and has monthly and quarterly prices. A Stripe
// subscription cannot mix billing intervals, so a yearly plan + bookings is not
// a valid combination.
function bookingsCompatible(data: OnboardingData): boolean {
  // Free has no Stripe plan line item, so a monthly bookings item is fine even
  // when a yearly cycle is set in the wizard (the cycle is ignored for Free).
  return data.billingCycle !== "yearly" || data.selectedPlan === "free";
}

// Free has no plan line item; its add-ons bill monthly whatever cycle is picked.
function addOnCycle(data: OnboardingData) {
  return data.selectedPlan === "free" ? "monthly" : data.billingCycle;
}

// Whether the selected plan can be bought on the selected cycle (Enterprise is
// not sold quarterly). Plans without Stripe prices (Free, Early Supporter) can.
export function planSoldOnCycle(data: OnboardingData): boolean {
  const plan = plans[data.selectedPlan];
  return !plan?.stripe || planPriceIdForCycle(plan, data.billingCycle) !== null;
}

export function computeTotal(data: OnboardingData): number {
  // Early Supporter grants are always comped.
  if (isEarlySupporter(data)) return 0;
  const plan = plans[data.selectedPlan];
  const planPrice = plan ? plan.price[data.billingCycle] ?? plan.price.monthly : 0;
  const addOnsTotal = data.selectedAddOns.reduce((sum, key) => {
    const a = addOns[key];
    if (!a) return sum;
    if (key === "bookings" && !bookingsCompatible(data)) return sum;
    return sum + a.price.monthly;
  }, 0);
  return planPrice + addOnsTotal;
}

// What the client is charged each billing period (S$540 a quarter for Starter).
export function computeChargePerCycle(data: OnboardingData): number {
  return computeTotal(data) * (data.selectedPlan === "free" ? 1 : CYCLE_MONTHS[data.billingCycle]);
}

// Builds the Stripe price IDs for the selected plan + add-ons.
export function buildPriceIds(data: OnboardingData): string[] {
  // Early Supporter is comped. No Stripe line items. An empty list routes the
  // checkout function down its free/no-Stripe path, where the grant is applied.
  if (isEarlySupporter(data)) return [];
  const ids: string[] = [];
  const plan = plans[data.selectedPlan];
  if (plan?.stripe) {
    const planPriceId = planPriceIdForCycle(plan, data.billingCycle);
    if (!planPriceId) throw new Error(`${plan.name} is not sold on ${data.billingCycle} billing.`);
    ids.push(planPriceId);
  }
  for (const key of data.selectedAddOns) {
    const a = addOns[key];
    if (!a) continue;
    if (key === "bookings" && !bookingsCompatible(data)) continue;
    const addOnPriceId = addOnPriceIdForCycle(a, addOnCycle(data));
    if (addOnPriceId) ids.push(addOnPriceId);
  }
  return ids;
}

export { plans, addOns, bookingsCompatible };
